import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyDirective } from 'ngx-currency';
import { AlertCardComponent } from '../../../../../shared/components/alert-card/alert-card.component';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { ControleEstoqueService } from '../../../../../shared/services/apis/produto/controle-estoque.service';
import { AtualizarMovimentacaoManualRequest } from '../../../../../shared/services/requests/produto/atualizar-movimentacao-manual.request';
import { ControleEstoqueDetalheMovimentoStruct } from '../../../../../shared/services/structs/produto/controle-estoque-detalhe-movimento.struct';
import { MaskService } from '../../../../../shared/services/mask.service';

export interface EditarMovimentacaoDialogData {
  row: ControleEstoqueDetalheMovimentoStruct;
}

@Component({
  selector: 'app-editar-movimentacao-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    NgxCurrencyDirective,
    AlertCardComponent,
  ],
  templateUrl: './editar-movimentacao-dialog.component.html',
  styleUrl: './editar-movimentacao-dialog.component.css',
})
export class EditarMovimentacaoDialogComponent {
  public isSalvando = false;
  public readonly hoje: Date = new Date();

  public readonly temLoteOuSerie: boolean;
  public dataMovimentacao: Date | null;
  public quantidade: number;
  public descricao: string;
  public quantidadeMask = this.maskService.getDynamicDecimalMask();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: EditarMovimentacaoDialogData,
    private dialogRef: MatDialogRef<EditarMovimentacaoDialogComponent>,
    private alertService: AlertService,
    private controleEstoqueService: ControleEstoqueService,
    private maskService: MaskService
  ) {
    this.temLoteOuSerie = !!(data.row.idLote || data.row.idSerie);
    this.dataMovimentacao = data.row.datetimeMovimentacao ? new Date(data.row.datetimeMovimentacao) : null;
    this.quantidade = data.row.quantidadeMovimentada;
    this.descricao = data.row.observacao || '';
  }

  public onDateInput(event: Event): void {
    MaskService.onDateInput(event);
  }

  public onDateKeydown(event: KeyboardEvent): void {
    const teclasPermitidas = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Enter'];
    const isAtalho = (event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase());
    if (teclasPermitidas.includes(event.key) || isAtalho) return;
    if (!/^\d$/.test(event.key)) event.preventDefault();
  }

  public onDataMovimentacaoBlur(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    const data = this.lerDataInput(input);
    if (data && data > this.hojeNormalizado()) {
      input.value = '';
      this.dataMovimentacao = null;
      this.alertService.show('Atenção', 'A data da movimentação não pode ser maior que a data atual.', AlertType.warning);
      return;
    }
    this.dataMovimentacao = data;
  }

  private lerDataInput(input: HTMLInputElement): Date | null {
    const valor = (input?.value || '').trim();
    if (!valor) return null;
    const apenasNumeros = valor.replace(/\D/g, '').substring(0, 8);
    if (apenasNumeros.length !== 8) {
      input.value = '';
      this.alertService.show('Atenção', 'Informe uma data válida no formato DD/MM/AAAA.', AlertType.warning);
      return null;
    }
    const formatado = `${apenasNumeros.substring(0, 2)}/${apenasNumeros.substring(2, 4)}/${apenasNumeros.substring(4, 8)}`;
    input.value = formatado;
    const data = this.parseDataBr(formatado);
    if (!data) {
      input.value = '';
      this.alertService.show('Atenção', 'Informe uma data válida no formato DD/MM/AAAA.', AlertType.warning);
    }
    return data;
  }

  private parseDataBr(valor: string): Date | null {
    const partes = valor.split('/');
    if (partes.length !== 3) return null;
    const dia = Number(partes[0]);
    const mes = Number(partes[1]);
    const ano = Number(partes[2]);
    if (!Number.isInteger(dia) || !Number.isInteger(mes) || !Number.isInteger(ano)) return null;
    if (ano < 1000 || ano > 9999 || mes < 1 || mes > 12 || dia < 1 || dia > 31) return null;
    const data = new Date(ano, mes - 1, dia);
    data.setHours(0, 0, 0, 0);
    if (data.getFullYear() !== ano || data.getMonth() !== mes - 1 || data.getDate() !== dia) return null;
    return data;
  }

  private hojeNormalizado(): Date {
    const h = new Date();
    h.setHours(0, 0, 0, 0);
    return h;
  }

  private formatarData(data: Date): string {
    return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}-${String(data.getDate()).padStart(2, '0')}`;
  }

  public fechar(): void {
    this.dialogRef.close();
  }

  public salvar(): void {
    if (!this.dataMovimentacao) {
      this.alertService.show('Atenção', 'Informe uma data válida para a movimentação.', AlertType.warning);
      return;
    }

    if (!this.temLoteOuSerie && !(this.quantidade > 0)) {
      this.alertService.show('Atenção', 'Informe uma quantidade maior que zero.', AlertType.warning);
      return;
    }

    const request: AtualizarMovimentacaoManualRequest = {
      dataMovimentacao: this.formatarData(this.dataMovimentacao),
      quantidade: this.temLoteOuSerie ? this.data.row.quantidadeMovimentada : this.quantidade,
      descricao: this.descricao.trim() || undefined,
    };

    this.isSalvando = true;
    this.controleEstoqueService.editarMovimentacao(this.data.row.idEstoqueMovimentacao, request).subscribe({
      next: response => {
        this.isSalvando = false;
        if (!response.isError) {
          this.dialogRef.close({ confirmed: true });
        } else {
          this.alertService.show('Atenção', response.errorDescription || 'Erro ao salvar a movimentação.', AlertType.warning);
        }
      },
      error: () => {
        this.isSalvando = false;
        this.alertService.show('Atenção', 'Erro ao salvar a movimentação.', AlertType.warning);
      },
    });
  }
}
